import React, { useEffect } from 'react';
import { FileText, ShoppingBag, RotateCcw, Shield, AlertCircle, Scale } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

/**
 * Terms and Conditions Page for Sunrise Bookstore
 * Includes Return & Refund Policy
 */

const TermsAndConditions: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <main className="min-h-screen bg-background text-text font-sans antialiased">
      <Helmet>
        <title>Terms & Conditions | Sunrise Bookstore</title>
        <meta name="description" content="Terms and conditions, return policy, and refund policy for Sunrise Bookstore." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 to-background py-16">
        <div className="container-custom max-w-5xl mx-auto px-4">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-6">
              <FileText className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
              Terms & Conditions
            </h1>
            <p className="text-lg text-primary-800 max-w-2xl mx-auto">
              Please read these terms carefully before using our services.
            </p>
            <p className="text-sm text-primary-600 mt-4">
              Last updated: December 25, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container-custom max-w-4xl mx-auto px-4">
          
          {/* Introduction */}
          <div className="mb-12">
            <p className="text-lg text-primary-900 leading-relaxed">
              Welcome to <strong>Sunrise Bookstore</strong>. By accessing our website and making a purchase, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, please do not use our services.
            </p>
          </div>

          {/* General Terms */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <Scale className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary">1. General Terms</h2>
            </div>
            
            <div className="space-y-4 pl-13 text-primary-800">
              <ul className="list-disc list-inside space-y-2">
                <li>You must be at least 18 years old to use our services or have parental consent.</li>
                <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                <li>You agree to provide accurate, current, and complete information during registration and checkout.</li>
                <li>We reserve the right to refuse service, terminate accounts, or cancel orders at our discretion.</li>
                <li>Prices and availability are subject to change without notice.</li>
              </ul>
            </div>
          </div>

          {/* Orders and Payment */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary">2. Orders and Payment</h2>
            </div>
            
            <div className="space-y-4 pl-13 text-primary-800">
              <div>
                <h3 className="text-xl font-semibold text-primary-900 mb-2">Order Confirmation</h3>
                <p>An order confirmation email does not constitute acceptance of your order. We reserve the right to accept or decline your order for any reason.</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-primary-900 mb-2">Payment</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Payment must be received before order processing.</li>
                  <li>We accept major credit/debit cards, UPI, net banking, and cash on delivery (where available).</li>
                  <li>All prices are in Indian Rupees (INR) and include applicable taxes unless stated otherwise.</li>
                  <li>Payment processing is handled securely through third-party payment gateways.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-primary-900 mb-2">Order Cancellation</h3>
                <p>You may cancel your order before it is shipped. Once shipped, please refer to our Return Policy below.</p>
              </div>
            </div>
          </div>

          {/* Shipping and Delivery */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-primary mb-4">3. Shipping and Delivery</h2>
            
            <div className="space-y-4 pl-13 text-primary-800">
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Delivery times:</strong> Estimated delivery times are 3-7 business days but may vary based on location.</li>
                <li><strong>Shipping charges:</strong> Free shipping on orders above ₹499. Standard shipping charges apply otherwise.</li>
                <li><strong>Risk of loss:</strong> All items purchased are at your risk after delivery.</li>
                <li><strong>Undeliverable orders:</strong> If a package is returned due to an incorrect address, you will be responsible for re-shipping costs.</li>
              </ul>
            </div>
          </div>

          {/* Return & Refund Policy - HIGHLIGHTED */}
          <div className="mb-12 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border-2 border-blue-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                <RotateCcw className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-3xl font-bold text-blue-900">4. Return & Refund Policy</h2>
            </div>
            
            <div className="space-y-6 text-primary-900">
              
              {/* Return Window */}
              <div>
                <h3 className="text-xl font-semibold text-blue-900 mb-3">Return Window</h3>
                <p className="mb-2">
                  You may return most items within <strong className="text-blue-700">7 days of delivery</strong> for a full refund or exchange, provided they meet our return conditions.
                </p>
              </div>

              {/* Eligible Items */}
              <div>
                <h3 className="text-xl font-semibold text-blue-900 mb-3">Eligible Items for Return</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Books and stationery in original, unused condition</li>
                  <li>Items with original packaging and tags intact</li>
                  <li>Products that are damaged, defective, or incorrect items delivered</li>
                </ul>
              </div>

              {/* Non-Returnable Items */}
              <div>
                <h3 className="text-xl font-semibold text-blue-900 mb-3">Non-Returnable Items</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Opened or used books (unless damaged/defective)</li>
                  <li>Items without original packaging or tags</li>
                  <li>Personalized or custom-made products</li>
                  <li>Sale or clearance items (marked as "Final Sale")</li>
                  <li>Digital products or gift cards</li>
                </ul>
              </div>

              {/* Return Process */}
              <div>
                <h3 className="text-xl font-semibold text-blue-900 mb-3">How to Return</h3>
                <ol className="list-decimal list-inside space-y-2">
                  <li><strong>Initiate return:</strong> Contact us at <a href="mailto:returns@sunrisebookstore.com" className="text-blue-600 hover:underline">returns@sunrisebookstore.com</a> or through your account dashboard</li>
                  <li><strong>Get approval:</strong> We'll review your request and provide a Return Authorization (RA) number</li>
                  <li><strong>Pack the item:</strong> Securely pack the item with all original materials</li>
                  <li><strong>Ship back:</strong> Use the prepaid shipping label we provide or arrange your own shipping</li>
                  <li><strong>Inspection:</strong> We'll inspect the returned item within 2-3 business days</li>
                </ol>
              </div>

              {/* Refund Process */}
              <div>
                <h3 className="text-xl font-semibold text-blue-900 mb-3">Refund Process</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li><strong>Processing time:</strong> Refunds are processed within 5-7 business days after receiving the returned item</li>
                  <li><strong>Refund method:</strong> Refunds are issued to the original payment method</li>
                  <li><strong>Shipping costs:</strong> Original shipping charges are non-refundable unless the return is due to our error</li>
                  <li><strong>Return shipping:</strong> We provide free return shipping for defective/damaged items. Other returns may incur return shipping costs.</li>
                </ul>
              </div>

              {/* Exchanges */}
              <div>
                <h3 className="text-xl font-semibold text-blue-900 mb-3">Exchanges</h3>
                <p>
                  We offer exchanges for defective or damaged items. Contact us to arrange an exchange, and we'll ship the replacement at no additional cost.
                </p>
              </div>

              {/* Damaged or Defective Items */}
              <div className="bg-white rounded-lg p-4 border-l-4 border-red-500">
                <h3 className="text-xl font-semibold text-red-700 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Damaged or Defective Items
                </h3>
                <p className="text-primary-800">
                  If you receive a damaged or defective item, please contact us within <strong>48 hours of delivery</strong> with photos of the damage. We'll arrange a replacement or full refund, including return shipping.
                </p>
              </div>

            </div>
          </div>

          {/* Intellectual Property */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-primary">5. Intellectual Property</h2>
            </div>
            
            <div className="space-y-3 pl-13 text-primary-800">
              <p>
                All content on this website, including text, images, logos, graphics, and software, is the property of Sunrise Bookstore or its licensors and is protected by copyright and trademark laws.
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>You may not reproduce, distribute, modify, or create derivative works without our written permission.</li>
                <li>Product images are for illustration purposes and may differ slightly from actual products.</li>
              </ul>
            </div>
          </div>

          {/* User Conduct */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-primary mb-4">6. User Conduct</h2>
            
            <div className="space-y-3 pl-13 text-primary-800">
              <p>When using our website, you agree NOT to:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Transmit harmful code, viruses, or malware</li>
                <li>Engage in fraudulent activities or misuse payment systems</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Scrape, crawl, or use automated tools to access our site without permission</li>
              </ul>
            </div>
          </div>

          {/* Limitation of Liability */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-primary mb-4">7. Limitation of Liability</h2>
            
            <div className="space-y-3 pl-13 text-primary-800">
              <p>
                To the fullest extent permitted by law, Sunrise Bookstore shall not be liable for any indirect, incidental, special, or consequential damages arising from:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Use or inability to use our website or services</li>
                <li>Errors or inaccuracies in content</li>
                <li>Unauthorized access to your account or data</li>
                <li>Third-party conduct or content</li>
              </ul>
              <p className="mt-3">
                Our total liability shall not exceed the amount paid by you for the product or service in question.
              </p>
            </div>
          </div>

          {/* Dispute Resolution */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-primary mb-4">8. Dispute Resolution</h2>
            
            <div className="space-y-3 pl-13 text-primary-800">
              <p>
                Any disputes arising from these Terms or your use of our services shall be resolved through:
              </p>
              <ol className="list-decimal list-inside space-y-2">
                <li><strong>Informal negotiation:</strong> Contact us to resolve the issue amicably</li>
                <li><strong>Mediation:</strong> If negotiation fails, we agree to mediation</li>
                <li><strong>Jurisdiction:</strong> All disputes shall be governed by the laws of India and subject to the exclusive jurisdiction of courts in [Your City]</li>
              </ol>
            </div>
          </div>

          {/* Changes to Terms */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-primary mb-4">9. Changes to Terms</h2>
            <div className="pl-13 text-primary-800">
              <p>
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on this page. Your continued use of our services constitutes acceptance of the updated Terms.
              </p>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8 mt-12">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-primary mb-3">Questions About Our Terms?</h2>
                <p className="text-primary-800 mb-4">
                  If you have questions about these Terms and Conditions or need assistance with a return, please contact us:
                </p>
                <div className="space-y-2 text-primary-900">
                  <p><strong>Email:</strong> <a href="mailto:support@sunrisebookstore.com" className="text-primary hover:underline">support@sunrisebookstore.com</a></p>
                  <p><strong>Returns Email:</strong> <a href="mailto:returns@sunrisebookstore.com" className="text-primary hover:underline">returns@sunrisebookstore.com</a></p>
                  <p><strong>Phone:</strong> +91 XXX-XXX-XXXX</p>
                  <p><strong>Address:</strong> Sunrise Bookstore, [Your Address]</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </main>
  );
};

export default TermsAndConditions;
